import { ResponsiveTable } from '../components/ResponsiveTable';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../utils/api';
import { useShopScopedPage } from '../hooks/useShopScopedReload';
import { ShopSelectionGate } from '../components/ShopSelectionGate';
import { Route, ChevronUp, ChevronDown, Filter, ExternalLink, Trash2 } from 'lucide-react';
import { ConfirmModal } from '../components/ConfirmModal';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { useAdminLogBulkDelete } from '../hooks/useAdminLogBulkDelete';
import { DateInput } from '../components/DateInput';

type JourneyStage =
  | 'STARTED'
  | 'CART'
  | 'IDENTITY'
  | 'SLOT_SELECTED'
  | 'PAYMENT_REVIEW'
  | 'BOOKING_CREATED'
  | 'CHECKOUT_REDIRECTED'
  | 'PAID';

type JourneyOutcome = 'IN_PROGRESS' | 'COMPLETED' | 'ABANDONED' | 'FAILED';

interface BookingJourneyRow {
  id: string;
  currentStage: JourneyStage;
  maxStageReached: JourneyStage;
  outcome: JourneyOutcome;
  email: string | null;
  phone: string | null;
  anonymousCode: string | null;
  snapshot: { cart?: Array<{ piercingTypeName?: string; bodyZoneName?: string; piercingTypeId?: string }> } | null;
  startedAt: string;
  lastSeenAt: string;
  shop: { id: string; name: string } | null;
  appointment: { id: string; status: string; depositStatus: string } | null;
}

interface BookingJourneyDetail extends BookingJourneyRow {
  events: Array<{
    id: string;
    eventType: string;
    stage: JourneyStage;
    occurredAt: string;
  }>;
}

const STAGES: JourneyStage[] = [
  'STARTED',
  'CART',
  'IDENTITY',
  'SLOT_SELECTED',
  'PAYMENT_REVIEW',
  'BOOKING_CREATED',
  'CHECKOUT_REDIRECTED',
  'PAID',
];

const SORT_FIELDS = [
  { value: 'lastSeenAt', labelKey: 'admin.bookingJourneys.sort.lastSeenAt' },
  { value: 'startedAt', labelKey: 'admin.bookingJourneys.sort.startedAt' },
  { value: 'currentStage', labelKey: 'admin.bookingJourneys.sort.stage' },
  { value: 'outcome', labelKey: 'admin.bookingJourneys.sort.outcome' },
] as const;

function cartSummary(snapshot: BookingJourneyRow['snapshot']): string {
  const cart = snapshot?.cart || [];
  if (cart.length === 0) return '—';
  return cart
    .map((item) => item.piercingTypeName || item.bodyZoneName || item.piercingTypeId || '—')
    .slice(0, 3)
    .join(', ');
}

function durationLabel(startedAt: string, lastSeenAt: string): string {
  const ms = new Date(lastSeenAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

export function BookingJourneysPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { needsShopSelection, canLoad, currentShopId } = useShopScopedPage();
  const [items, setItems] = useState<BookingJourneyRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [selected, setSelected] = useState<BookingJourneyDetail | null>(null);
  const [filters, setFilters] = useState({
    stage: '',
    outcome: '',
    unpaid: '',
    dateFrom: '',
    dateTo: '',
    limit: '50',
    offset: '0',
    sortBy: 'lastSeenAt',
    sortOrder: 'desc',
  });

  const loadJourneys = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const response = await apiClient(`/api/admin/booking-journeys?${params}`);
      if (!response.ok) {
        throw new Error(response.status === 403 ? t('admin.bookingJourneys.forbidden') : t('admin.bookingJourneys.loadError'));
      }
      const data = await response.json();
      if (data.success) {
        setItems(data.data.items || []);
        setTotal(data.data.total || 0);
      }
    } catch (error) {
      console.error('Failed to load booking journeys:', error);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, canLoad, t]);

  useEffect(() => {
    loadJourneys();
  }, [loadJourneys, currentShopId]);

  const visibleIds = useMemo(() => items.map((item) => item.id), [items]);
  const {
    selectedArray,
    selectedCount,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleIds);

  const bulkDelete = useAdminLogBulkDelete({
    softDeletePath: '/api/admin/booking-journeys/bulk-soft-delete',
    hardDeletePath: '/api/admin/booking-journeys/bulk-hard-delete',
    i18n: {
      error: 'admin.bookingJourneys.bulkDelete.error',
      partialSoft: 'admin.bookingJourneys.bulkDelete.partialSoft',
      partialHard: 'admin.bookingJourneys.bulkDelete.partialHard',
      softSuccess: 'admin.bookingJourneys.bulkDelete.softSuccess',
      hardSuccess: 'admin.bookingJourneys.bulkDelete.hardSuccess',
      softTitle: 'admin.bookingJourneys.bulkDelete.softTitle',
      softMessage: 'admin.bookingJourneys.bulkDelete.softMessage',
      entityLabel: 'admin.bookingJourneys.bulkDelete.entityLabel',
    },
    onComplete: async () => {
      clearSelection();
      await loadJourneys();
    },
  });

  const formatDateTime = (dateTime: string) =>
    new Date(dateTime).toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

  const openDetail = async (id: string) => {
    const response = await apiClient(`/api/admin/booking-journeys/${id}`);
    const data = await response.json();
    if (data.success) setSelected(data.data);
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (filters.sortBy !== field) return null;
    return filters.sortOrder === 'asc' ? (
      <ChevronUp className="inline w-3 h-3 ml-1" />
    ) : (
      <ChevronDown className="inline w-3 h-3 ml-1" />
    );
  };

  if (needsShopSelection) {
    return <ShopSelectionGate />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-textPrimary flex items-center gap-2">
            <Route className="w-6 h-6" />
            {t('admin.bookingJourneys.title')}
          </h1>
          <p className="text-sm text-textSecondary mt-1">{t('admin.bookingJourneys.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          className="btn-secondary inline-flex items-center gap-2"
        >
          <Filter className="w-4 h-4" />
          {t('admin.bookingJourneys.filters')}
        </button>
      </div>

      {showFilters && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">{t('admin.bookingJourneys.filter.stage')}</label>
              <select
                value={filters.stage}
                onChange={(e) => setFilters({ ...filters, stage: e.target.value, offset: '0' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">{t('admin.bookingJourneys.filter.all')}</option>
                {STAGES.map((stage) => (
                  <option key={stage} value={stage}>{t(`admin.bookingJourneys.stage.${stage}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">{t('admin.bookingJourneys.filter.outcome')}</label>
              <select
                value={filters.outcome}
                onChange={(e) => setFilters({ ...filters, outcome: e.target.value, unpaid: '', offset: '0' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">{t('admin.bookingJourneys.filter.all')}</option>
                <option value="IN_PROGRESS">{t('admin.bookingJourneys.outcome.IN_PROGRESS')}</option>
                <option value="COMPLETED">{t('admin.bookingJourneys.outcome.COMPLETED')}</option>
                <option value="ABANDONED">{t('admin.bookingJourneys.outcome.ABANDONED')}</option>
                <option value="FAILED">{t('admin.bookingJourneys.outcome.FAILED')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">{t('admin.bookingJourneys.filter.unpaid')}</label>
              <select
                value={filters.unpaid}
                onChange={(e) => setFilters({ ...filters, unpaid: e.target.value, outcome: '', offset: '0' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">{t('admin.bookingJourneys.filter.all')}</option>
                <option value="true">{t('admin.bookingJourneys.filter.unpaidYes')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">{t('admin.bookingJourneys.filter.dateFrom')}</label>
              <DateInput
                value={filters.dateFrom}
                onChange={(value) => setFilters({ ...filters, dateFrom: value, offset: '0' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">{t('admin.bookingJourneys.filter.dateTo')}</label>
              <DateInput
                value={filters.dateTo}
                onChange={(value) => setFilters({ ...filters, dateTo: value, offset: '0' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">{t('admin.bookingJourneys.filter.sortBy')}</label>
              <select
                value={filters.sortBy}
                onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {SORT_FIELDS.map((f) => (
                  <option key={f.value} value={f.value}>{t(f.labelKey)}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap justify-between items-center gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-textSecondary">
              {t('admin.bookingJourneys.showing', { count: items.length, total, offset: parseInt(filters.offset, 10) || 0 })}
            </p>
            {selectedCount > 0 && (
              <span className="text-sm text-primary-600 font-medium">
                {t('admin.bookingJourneys.bulkDelete.selected', { count: selectedCount })}
              </span>
            )}
          </div>
          {selectedCount > 0 && (
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => bulkDelete.setSoftDeleteModalOpen(true)} className="btn-secondary text-sm inline-flex items-center gap-1.5">
                <Trash2 className="w-4 h-4" />
                {t('admin.bookingJourneys.bulkDelete.soft', { count: selectedCount })}
              </button>
              {bulkDelete.canHardDelete && (
                <button type="button" onClick={() => bulkDelete.setHardDeleteModalOpen(true)} className="text-sm inline-flex items-center gap-1.5 px-3 py-2 border border-red-300 text-red-700 rounded-lg">
                  <Trash2 className="w-4 h-4" />
                  {t('admin.bookingJourneys.bulkDelete.hard', { count: selectedCount })}
                </button>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="p-12 text-center text-textSecondary">{t('admin.bookingJourneys.loading')}</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-textSecondary">{t('admin.bookingJourneys.empty')}</div>
        ) : (
          <div className="overflow-x-auto">
            <ResponsiveTable className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600"
                      aria-label={t('admin.bookingJourneys.bulkDelete.selectAll')}
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-textPrimary uppercase">
                    <button onClick={() => setFilters((prev) => ({ ...prev, sortBy: 'lastSeenAt', sortOrder: prev.sortOrder === 'desc' ? 'asc' : 'desc' }))}>
                      {t('admin.bookingJourneys.sort.lastSeenAt')}
                      <SortIcon field="lastSeenAt" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-textPrimary uppercase">{t('admin.bookingJourneys.columns.stage')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-textPrimary uppercase">{t('admin.bookingJourneys.columns.outcome')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-textPrimary uppercase">{t('admin.bookingJourneys.columns.shop')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-textPrimary uppercase">{t('admin.bookingJourneys.columns.contact')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-textPrimary uppercase">{t('admin.bookingJourneys.columns.cart')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-textPrimary uppercase">{t('admin.bookingJourneys.columns.duration')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-textPrimary uppercase">{t('admin.bookingJourneys.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((row) => (
                  <tr key={row.id} className={selectedArray.includes(row.id) ? 'bg-primary-50/40' : ''}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedArray.includes(row.id)}
                        onChange={() => toggleSelect(row.id)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-textSecondary whitespace-nowrap">{formatDateTime(row.lastSeenAt)}</td>
                    <td className="px-4 py-3 text-sm">{t(`admin.bookingJourneys.stage.${row.currentStage}`)}</td>
                    <td className="px-4 py-3 text-sm">{t(`admin.bookingJourneys.outcome.${row.outcome}`)}</td>
                    <td className="px-4 py-3 text-sm">{row.shop?.name || '—'}</td>
                    <td className="px-4 py-3 text-sm">
                      {row.anonymousCode
                        ? t('admin.bookingJourneys.anonymous', { code: row.anonymousCode })
                        : [row.email, row.phone].filter(Boolean).join(' · ') || t('admin.bookingJourneys.unidentified')}
                    </td>
                    <td className="px-4 py-3 text-sm max-w-xs truncate">{cartSummary(row.snapshot)}</td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">{durationLabel(row.startedAt, row.lastSeenAt)}</td>
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => openDetail(row.id)} className="text-primary-600 text-sm inline-flex items-center gap-1">
                        <ExternalLink className="w-4 h-4" />
                        {t('admin.bookingJourneys.viewDetails')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">{t('admin.bookingJourneys.detailTitle')}</h2>
            <div className="flex flex-wrap gap-1 mb-4">
              {STAGES.map((stage) => {
                const reached = STAGES.indexOf(stage) <= STAGES.indexOf(selected.maxStageReached);
                const current = stage === selected.currentStage;
                return (
                  <span
                    key={stage}
                    className={`px-2 py-1 text-xs rounded ${current ? 'bg-primary-600 text-white' : reached ? 'bg-primary-100 text-primary-800' : 'bg-gray-100 text-gray-500'}`}
                  >
                    {t(`admin.bookingJourneys.stage.${stage}`)}
                  </span>
                );
              })}
            </div>
            <p className="text-sm mb-2">{t(`admin.bookingJourneys.outcome.${selected.outcome}`)}</p>
            {selected.appointment?.id && (
              <button
                type="button"
                className="text-sm text-primary-600 mb-4"
                onClick={() => navigate(`/appointments/${selected.appointment!.id}`)}
              >
                {t('admin.bookingJourneys.openAppointment')}
              </button>
            )}
            <ul className="space-y-2 text-sm">
              {selected.events?.map((event) => (
                <li key={event.id} className="border-b border-gray-100 pb-2">
                  <span className="text-textSecondary">{formatDateTime(event.occurredAt)}</span>
                  {' — '}
                  {event.eventType}
                  {' · '}
                  {t(`admin.bookingJourneys.stage.${event.stage}`)}
                </li>
              ))}
            </ul>
            <div className="mt-4 text-right">
              <button type="button" className="btn-secondary" onClick={() => setSelected(null)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={bulkDelete.softDeleteModalOpen}
        title={t(bulkDelete.i18n.softTitle)}
        message={t(bulkDelete.i18n.softMessage, { count: selectedCount })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        confirmVariant="warning"
        isLoading={bulkDelete.deleteLoading}
        onConfirm={() => bulkDelete.confirmBulkSoftDelete(selectedArray)}
        onClose={() => !bulkDelete.deleteLoading && bulkDelete.setSoftDeleteModalOpen(false)}
      />
      <ConfirmDefinitiveDeleteModal
        isOpen={bulkDelete.hardDeleteModalOpen}
        onConfirm={() => bulkDelete.confirmBulkHardDelete(selectedArray)}
        onClose={() => !bulkDelete.deleteLoading && bulkDelete.setHardDeleteModalOpen(false)}
        entityLabel={t(bulkDelete.i18n.entityLabel)}
        count={selectedCount}
        isLoading={bulkDelete.deleteLoading}
      />
      {bulkDelete.toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm text-white ${
            bulkDelete.toast.type === 'success'
              ? 'bg-green-600'
              : bulkDelete.toast.type === 'warning'
                ? 'bg-amber-600'
                : 'bg-red-600'
          }`}
        >
          {bulkDelete.toast.message}
        </div>
      )}
    </div>
  );
}
